import {
  Component,
  Host,
  h,
  Element,
  Listen,
  Event,
  EventEmitter,
  State,
  Method,
} from '@stencil/core';
import { handleKeyboard } from '../../utils/keyboard';
import { positiveMod } from '../../utils/positiveMod';
import { computeOverflowTabs } from './overflow-manager';

export interface AhaTabList extends HTMLAhaTabListElement {}

/**
 * `aha-tab-list` is a container of one or many `aha-tab` components and can be used inside `aha-tabs` to create smart tabs.
 * It acts as a `tablist` by default or a `nav` if any `aha-tab` has a href attribute.
 * @slot DEFAULT - Contents of `aha-tab-list`
 */
@Component({
  tag: 'aha-tab-list',
  styleUrl: 'aha-tab-list.css',
  shadow: true,
})
export class AhaTabList {
  @Element() host!: HTMLAhaTabListElement;

  private tabs: HTMLAhaTabElement[] = [];
  private visibleTabs: HTMLAhaTabElement[] = [];

  private resizeObserver?: ResizeObserver;
  private tabWidths: number[] = [];
  private width: number = 0;
  private visible?: HTMLDivElement;
  private menu?: HTMLDivElement;
  private menuButton?: HTMLElement;
  @State() overflowing = false;

  /** @ignore */
  @Event() tabChosenAtIndex!: EventEmitter<number>;

  private loaded = false;
  private loadCallbacks: (() => void)[] = [];

  private handleKeyDown = (event: KeyboardEvent) => {
    handleKeyboard(event, {
      ArrowLeft: () => {
        if (this.focusedIndex === null) {
          this.focusTabAtIndex(0);
        } else {
          this.focusTabAtIndex(this.focusedIndex - 1);
        }
      },
      ArrowRight: () => {
        if (this.focusedIndex === null) {
          this.focusTabAtIndex(0);
        } else {
          this.focusTabAtIndex(this.focusedIndex + 1);
        }
      },
      Home: () => {
        this.focusTabAtIndex(0);
      },
      End: () => {
        this.focusTabAtIndex(-1);
      },
    });
  };

  private focusTabAtIndex = (index: number) => {
    const item = this.focusableItems[positiveMod(index, this.focusableItems.length)];
    item?.focus();
  };

  private get focusedIndex() {
    const index = this.focusableItems.indexOf(this.focusedItem!);
    if (index < 0) return null;
    return index;
  }

  private get focusedItem() {
    const query = 'aha-button:focus-within, aha-tab:focus';
    return (
      this.host.shadowRoot?.querySelector<HTMLElement>(query) ||
      this.host.querySelector<HTMLElement>(query)
    );
  }

  private get focusableItems() {
    const items = [...this.visibleTabs] as HTMLElement[];
    if (this.overflowing) items.push(this.menuButton!);
    return items;
  }

  @Listen('tabChosen')
  handleTabChosen(event: CustomEvent<HTMLAhaTabElement>) {
    event.stopPropagation();

    const chosenTab = event.detail;
    const indexOfTab = this.tabs.indexOf(chosenTab);
    this.tabChosenAtIndex.emit(indexOfTab);
  }

  /** @ignore */
  @Method()
  async selectTabAtIndex(index: number) {
    const chosenTab = this.tabs[index];

    this.tabs.forEach(tab => {
      tab.selected = tab === chosenTab;
    });

    requestAnimationFrame(() => this.distributeTabs());
  }

  componentDidLoad() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.host);

    this.loaded = true;
    setTimeout(() => {
      for (const callback of this.loadCallbacks) {
        callback();
      }
      this.loadCallbacks = [];
    }, 0);
  }

  public runOnceLoaded(callback: () => void) {
    if (this.loaded) {
      callback();
    } else {
      this.loadCallbacks.push(callback);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.unobserve(this.host);
  }

  private handleResize: ResizeObserverCallback = (entries, _observer) => {
    if (entries.length === 0) return;
    this.width = entries[0].contentRect.width;
    this.distributeTabs();
  };

  private handleSlotChange = () => {
    const incomingTabs = this.host
      .shadowRoot!.querySelector('slot')!
      .assignedElements({ flatten: true })
      .filter(element => element.tagName === 'AHA-TAB') as HTMLAhaTabElement[];
    if (incomingTabs.length === 0) return;

    this.tabs = incomingTabs;

    this.tabWidths = this.tabs.map(tab => tab.getBoundingClientRect().width);

    this.distributeTabs();
  };

  private distributeTabs = () => {
    this.menu!.innerHTML = '';

    const { selectedTab, visibleTabs, overflowTabs } = computeOverflowTabs(
      this.tabs,
      this.tabWidths,
      this.width - 15,
    );

    this.visibleTabs = visibleTabs;

    const tabWasFocused = selectedTab?.matches(':focus');

    function wrapInMenuItem(tab: HTMLAhaTabElement) {
      tab.inMenu = true;

      const menuItem = document.createElement('aha-menu-item');
      menuItem.appendChild(tab);
      menuItem.addEventListener('click', event => {
        if (event.target === menuItem) tab.click();
      });
      return menuItem;
    }

    this.visibleTabs.forEach(tab => {
      tab.inMenu = false;
      this.visible!.appendChild(tab);
    });
    overflowTabs.map(wrapInMenuItem).forEach(tab => this.menu!.appendChild(tab));

    this.overflowing = overflowTabs.length > 0;

    // Sometimes all this shuffling causes the selected tab to lose focus in this process
    if (tabWasFocused) selectedTab?.focus();
  };

  private get shouldActAsNavigation() {
    return this.tabs.some(tab => tab.href);
  }

  render() {
    return (
      <Host onKeyDown={this.handleKeyDown} role={this.shouldActAsNavigation ? 'nav' : 'tablist'}>
        <slot onSlotchange={this.handleSlotChange}></slot>
        <div ref={ref => (this.visible = ref)} class="tab-list-container"></div>
        <div style={{ marginLeft: 'auto' }}></div>
        <div hidden={!this.overflowing}>
          <aha-menu>
            <aha-button
              kind="ghost"
              slot="button"
              ref={(ref?: HTMLAhaButtonElement) => (this.menuButton = ref)}
            >
              <aha-icon icon="fas fa-ellipsis"></aha-icon>
            </aha-button>
            <div ref={ref => (this.menu = ref)}></div>
          </aha-menu>
        </div>
      </Host>
    );
  }
}
